import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

// Créer le contexte d'authentification
const AuthContext = createContext(null);

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => useContext(AuthContext);

// Provider du contexte
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Initialiser l'utilisateur au chargement
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // Vérifier la validité de la session en appelant l'API via axios (utilise les cookies)
        const response = await authService.getCurrentUser() 
          ? await import('../services/api').then(m => m.default.get('/auth/me'))
          : null;
        
        if (response && response.status === 200) {
          setCurrentUser(response.data.data);
        } else {
          // Utilisateur non trouvé ou session invalide
          localStorage.removeItem('user');
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Erreur lors de la vérification de la session:', error);
        // En cas d'erreur (401 etc), nettoyer le localStorage
        localStorage.removeItem('user');
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuthStatus();
  }, []);
  
  // Fonction de connexion
  const login = async (credentials) => {
    try {
      console.log('Attempting login...');
      
      const data = await authService.login(credentials);
      console.log('Login response:', data);
      
      setCurrentUser(data.user);
      // Stocker l'utilisateur uniquement (le token est dans le cookie)
      // localStorage.setItem est déjà géré par authService.login
      console.log('Login successful');
      
      return { success: true, data };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Erreur de connexion'
      };
    }
  };
  
  // CORRECTION: Fonction de déconnexion améliorée
  const logout = async () => {
    try {
      console.log('Début de la déconnexion...');
      
      // Nettoyer immédiatement l'état local
      setCurrentUser(null);
      
      // Nettoyer le localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('maintain_session'); // Nettoyer aussi cette clé
      
      // Tenter d'appeler l'API pour invalider le token côté serveur
      try {
        await authService.logout();
        console.log('Déconnexion API réussie');
      } catch (apiError) {
        // Même si l'API échoue, continuer la déconnexion locale
        console.log('Erreur API logout (non critique):', apiError);
      }
      
      console.log('Déconnexion terminée avec succès');
      
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      
      // En cas d'erreur, forcer la déconnexion locale
      setCurrentUser(null);
      localStorage.clear(); // Nettoyer tout le localStorage en cas de problème
    }
  };
  
  // Méthode pour vérifier si l'utilisateur a une permission spécifique
  const hasPermission = (permission) => {
    if (!currentUser) return false;
    
    // Les administrateurs ont toutes les permissions
    if (currentUser.role === 'admin') return true;
    
    // Vérifier la permission spécifique
    return currentUser.permissions && currentUser.permissions[permission];
  };
  
  // Méthode pour mettre à jour l'utilisateur courant
  const updateCurrentUser = (userData) => {
    const updatedUser = { ...currentUser, ...userData };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setCurrentUser(updatedUser);
  };
  
  // SUPPRESSION: Retrait de la logique maintain_session qui causait des problèmes
  
  // Valeur du contexte
  const value = {
    currentUser,
    loading,
    login,
    logout,
    hasPermission,
    updateCurrentUser,
    isAuthenticated: !!currentUser
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};